/**
 * Breadcrumb Component
 * 
 * Reusable breadcrumb navigation
 * 
 * Version: 6.2.0
 */
import React, { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import './Breadcrumb.css'

export interface BreadcrumbItem {
  label: string
  path?: string
  icon?: ReactNode
  onClick?: () => void
}

interface BreadcrumbProps {
  items: BreadcrumbItem[]
  separator?: ReactNode
  maxItems?: number
  showHome?: boolean
  className?: string
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  separator = '/',
  maxItems,
  showHome = true,
  className = ''
}) => {
  let displayItems = items
  
  // Truncate middle items if maxItems is set
  if (maxItems && items.length > maxItems) {
    const firstItem = items[0]
    const lastItems = items.slice(-(maxItems - 1))
    displayItems = [
      firstItem,
      { label: '...', onClick: undefined },
      ...lastItems
    ]
  }

  const renderSeparator = () => (
    <span className="breadcrumb-separator">{separator}</span>
  )

  const renderItem = (item: BreadcrumbItem, index: number, isLast: boolean) => {
    const content = (
      <>
        {item.icon && <span className="breadcrumb-icon">{item.icon}</span>}
        <span className="breadcrumb-label">{item.label}</span>
      </>
    )

    const className = `breadcrumb-item ${isLast ? 'active' : ''} ${item.onClick ? 'clickable' : ''}`

    if (isLast || !item.path) {
      return (
        <span key={index} className={className}>
          {content}
        </span>
      )
    }

    if (item.onClick) {
      return (
        <button 
          key={index} 
          className={className}
          onClick={item.onClick}
        >
          {content}
        </button>
      )
    }

    return (
      <Link key={index} to={item.path} className={className}>
        {content}
      </Link>
    )
  }

  return (
    <nav className={`breadcrumb ${className}`} aria-label="Breadcrumb">
      {showHome && (
        <>
          <Link to="/" className="breadcrumb-item home">
            <span className="breadcrumb-icon">🏠</span>
            <span className="breadcrumb-label">Home</span>
          </Link>
          {renderSeparator()}
        </>
      )}
      
      {displayItems.map((item, index) => (
        <React.Fragment key={index}>
          {renderItem(item, index, index === displayItems.length - 1)}
          {index < displayItems.length - 1 && renderSeparator()}
        </React.Fragment>
      ))}
    </nav>
  )
}

// Auto-generate breadcrumbs from current path
interface AutoBreadcrumbProps {
  className?: string
}

export const AutoBreadcrumb: React.FC<AutoBreadcrumbProps> = ({ className = '' }) => {
  const pathSegments = window.location.pathname.split('/').filter(Boolean)
  
  if (pathSegments.length === 0) return null

  const items: BreadcrumbItem[] = pathSegments.map((segment, index) => {
    const path = '/' + pathSegments.slice(0, index + 1).join('/')
    const label = segment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
    
    return { label, path }
  })

  return <Breadcrumb items={items} className={className} />
}

export default Breadcrumb